import { LabelValueTextProps } from '@components/LabelValueText/LabelValueText';
import { User } from '@features/api/response/User';
import { QuestionnaireTemplate } from '@features/api/response/questionnaire';
import GlobalIntlSingleton from '@setup/intl/GlobalIntlSingleton';
import moment from 'moment';

const getBooleanPropValue = (value?: boolean) => {
  if (value === undefined || value === null) return value;

  return GlobalIntlSingleton.intl.formatMessage({
    id: `misc.${value ? 'yes' : 'no'}`,
  });
};

export const getPersonalInfo = (user: User): LabelValueTextProps[] => {
  return [
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.firstNames',
      }),
      value: user.basicDetails.firstName,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.middleNames',
      }),
      value: user.basicDetails.middleName,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.lastNames',
      }),
      value: user.basicDetails.lastName,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'signUp.form.birthDate',
      }),
      value: moment(user.basicDetails.dateOfBirth, 'YYYY-MM-DD').format(
        'DD/MM/YYYY'
      ),
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.emailAddress',
      }),
      value: user.email,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.contactDetails.mobileNumber',
      }),
      value: `${user.contactDetails?.phoneCountryCode} ${user.contactDetails?.phoneNumber}`,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.address',
      }),
      value: [
        user.contactDetails?.streetAddress,
        user.contactDetails?.city,
        user.contactDetails?.state,
        user.contactDetails?.zipCode,
        user.contactDetails?.country,
      ]
        .filter((x) => x)
        .join(', '),
    },
  ];
};

export const getTaxDetails = (user: User): LabelValueTextProps[] => {
  const taxDetails: LabelValueTextProps[] = [
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.taxResidence',
      }),
      value: user.taxDetails?.taxResidenceCountry,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.taxIdentificationNumber',
      }),
      value: user.taxDetails?.primaryTin,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.taxResidenceSecondary',
      }),
      value: user.taxDetails?.secondaryTaxResidenceCountry,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.taxIdentificationNumberSecondary',
      }),
      value: user.taxDetails?.secondaryTin,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.usTaxResident',
      }),
      value: getBooleanPropValue(user.taxDetails?.usTaxPayer),
    },
  ];
  if (user.taxDetails?.noPrimaryTinReason) {
    taxDetails.splice(2, 0, {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.primaryTinExcuse',
      }),
      value: user.taxDetails?.noPrimaryTinReason,
    });
  }
  if (user.taxDetails?.noSecondaryTinReason) {
    taxDetails.splice(user.taxDetails?.noSecondaryTinReason ? 5 : 4, 0, {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.secondaryTinExcuse',
      }),
      value: user.taxDetails?.noSecondaryTinReason,
    });
  }
  return taxDetails;
};

export const getBankDetails = (user: User): LabelValueTextProps[] => {
  return [
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.accountHoldersName',
      }),
      value: user.bankDetails?.accountHolderName,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.bankDetails.bankName',
      }),
      value: user.bankDetails?.bankName,
    },

    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'profile.bankLocation',
      }),
      value: user.bankDetails?.bankLocation,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.bankDetails.iban',
      }),
      value: user.bankDetails?.iban,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.bankDetails.bankAccountNumber',
      }),
      value: user.bankDetails?.accountNumber,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.bankDetails.swiftAndBic',
      }),
      value: user.bankDetails?.swiftOrBic,
    },
    {
      label: GlobalIntlSingleton.intl.formatMessage({
        id: 'investment.bankDetails.nominee',
      }),
      value: getBooleanPropValue(
        typeof user.bankDetails?.soleAccountOwner === 'boolean'
          ? !user.bankDetails?.soleAccountOwner
          : undefined
      ),
    },
  ];
};

export const getQuestionnaireAnswers = (
  questionnaireTemplate: QuestionnaireTemplate | undefined,
  questionnaireData: { [key: string]: string[] | string }
): LabelValueTextProps[] => {
  if (!questionnaireTemplate) return [];

  return questionnaireTemplate.fields.map((field) => ({
    label: field.title,
    value: Array.isArray(questionnaireData[field.id])
      ? (questionnaireData[field.id] as string[]).join(', ')
      : (questionnaireData[field.id] as string),
  }));
};
